/**
 * Shared tooltip bodies for the profiler export buttons.
 *
 * TD-2648: the download menu always opens, and an export the caller cannot run
 * is listed disabled with a message saying why. Two distinct reasons, and they
 * must not be conflated:
 *
 *   - `profilerexportdisabled` is set. A deliberate withdrawal from an account
 *     that may well be on a paid tier, so it is never an upgrade prompt.
 *   - The role is not entitled to that artefact. Deliberately generic and does
 *     not name a tier, so it stays correct if the tiers are renamed and does not
 *     read as a missing permission.
 *
 * `exportBlockedNode` is the single place the precedence between the two lives:
 * the withdrawal wins over the entitlement message.
 */

export const DOWNLOADS_DEACTIVATED_NODE = (
  <>
    Downloads deactivated.
    <br />
    Contact <a href="mailto:support@transact.digital">support@transact.digital</a> for assistance.
  </>
);

export const EXPORT_NOT_IN_PLAN_NODE = (
  <>
    Not included in your current plan.
    <br />
    Contact <a href="mailto:support@transact.digital">support@transact.digital</a> to add it.
  </>
);

/**
 * The tooltip body for an export the caller cannot run.
 *
 * @param {boolean} isExportDisabled - Whether the caller carries `profilerexportdisabled`
 * @returns {React.ReactNode}
 */
export const exportBlockedNode = (isExportDisabled) =>
  isExportDisabled ? DOWNLOADS_DEACTIVATED_NODE : EXPORT_NOT_IN_PLAN_NODE;

/**
 * Binds the caller's export-disabled state once, returning the predicate each
 * download entry uses to decide whether it can be run at all. Pair it with
 * `exportBlockedNode` above: this answers "blocked?", that answers "why?".
 *
 * Entitlement only. Conditions that are not about entitlement (no revenue, no
 * contacts, list too long, download limit reached) stay with their own checks
 * and keep their own messages.
 *
 * @param {boolean} isExportDisabled - Whether the caller carries `profilerexportdisabled`
 * @returns {(hasRoleAccess: boolean) => boolean}
 */
export const exportBlockedFor = (isExportDisabled) => (hasRoleAccess) =>
  isExportDisabled || !hasRoleAccess;

/**
 * Builds disabled, tooltip, and no-access props for a profiler export entry.
 * Collapses the repeated isExportBlocked + exportBlockedNode pair that Sonar
 * flagged as duplicated new code across SearchResults and ProfilerProjectView.
 *
 * @param {boolean} isExportDisabled - Whether the caller carries `profilerexportdisabled`
 * @param {boolean} hasRoleAccess - Whether the caller's role entitles this export
 * @param {boolean} [extraDisabled=false] - Non-entitlement disable (limit reached, loading, etc.)
 * @returns {{ disabled: boolean, tooltipClickable: boolean, hasAccess: boolean, noAccessNode: React.ReactNode }}
 */
export const exportEntryGate = ({ isExportDisabled, hasRoleAccess, extraDisabled = false }) => {
  const blocked = exportBlockedFor(isExportDisabled)(hasRoleAccess);
  return {
    disabled: extraDisabled || blocked,
    tooltipClickable: blocked,
    hasAccess: !blocked,
    noAccessNode: exportBlockedNode(isExportDisabled)
  };
};
